import React, { useCallback, useMemo, useRef } from 'react';
import {
  useVirtualizedMeasurer,
  useVirtualizedScrollHandler,
  useVirtualizedState,
} from '../virtualized';
import { BindVirtualizedList } from './bind-virtualized-list';
import { UseVirtualizedListOptions } from './use-virtualized-list-options';

/**
 * Default component to use when rendering the scrollable area.
 */
const DEFAULT_COMPONENT = 'div';

/**
 * React hook which virtualizes a set of React Elements in vertical scrolling
 * list.
 * @param elements The React elements to virtualize.
 * @param options
 */
export const useVirtualizedList = <T extends HTMLElement = HTMLElement>(
  elements: React.ReactElement[] = [],
  options: UseVirtualizedListOptions<T> = {},
): BindVirtualizedList<T> => {
  // Compute the option values, using defaults as required.
  const {
    batchSize,
    initialBatchSize,
    component: Component = DEFAULT_COMPONENT,
    threshold,
    overscan,
  } = options;

  const scrollable = options.ref || useRef();

  // Ref for the container (parent element) for visible virtualized children.
  const container = useRef(null);

  // Virtualized state object which tracks settings, sizes, positions, and the
  // set of currently visible virtualized nodes.
  const state = useVirtualizedState(elements, {
    batchSize,
    initialBatchSize,
    overscan,
    threshold,
  });

  // Virtualized scroll handler which handles scroll events on the parent
  // scrollable container element and updates the virtualized state whenever
  // a scroll event occurs.
  const virtualizedScrollHandler = useVirtualizedScrollHandler(
    scrollable,
    container,
    state,
    'vertical',
  );

  // Use the virtualized measurer which measures children as they change,
  // updating the state as required.
  useVirtualizedMeasurer(scrollable, container, state, 'vertical');

  // Convenience style properties for the scroll container.
  const style = useMemo(
    (): React.CSSProperties => ({
      overflow: 'scroll',
      position: 'relative',
    }),
    [],
  );

  // CSS properties to apply to the container node that holds the virtualized
  // children.
  const containerStyle = useMemo(
    (): React.CSSProperties => ({
      height: `${state.size}px`,
      position: 'absolute',
      width: '100%',
    }),
    [state.size],
  );

  // Binding scroll handler which should be attached to the parent, scrollable
  // component/node.
  const onScroll = useCallback(
    (e: React.UIEvent<T>): void => {
      virtualizedScrollHandler(e);
      if (options.onScroll) {
        options.onScroll(e);
      }
    },
    [options.onScroll, virtualizedScrollHandler],
  );

  // Set of properties to apply to the container node.
  const componentProps = {
    className: 'virtualized',
    style: containerStyle,
    ref: container,
  };

  // New resulting children that is a wrapper node along with all virtualized
  // children.
  const children = (
    <Component {...componentProps}>{state.virtualizedChildren}</Component>
  );

  return {
    children,
    onScroll,
    ref: scrollable,
    style,
  };
};
